import { useCallback, useEffect, useId, useRef } from "react";
import { useMutation, useQueryClient } from "@origin-digital/query";
import { submitJointAccountHolder } from "../../services/jointAccountHolder";
import { HolderDetails } from "./validation";

const submissionError = () =>
  new Error("Unable to confirm joint account holder submission");

export const useSubmitJointAccountHolder = () => {
  const client = useQueryClient();
  const id = useId();
  const input = useRef<{ accounts: string[]; details: HolderDetails }>();
  const inFlight = useRef(false);
  const removeCachedMutation = useCallback(() => {
    const cache = client.getMutationCache();
    cache
      .findAll({ mutationKey: ["joint-account-holder", id], exact: true })
      .forEach((mutation) => cache.remove(mutation));
  }, [client, id]);
  const { mutateAsync, reset, isPending } = useMutation<void, Error, void>({
    mutationKey: ["joint-account-holder", id],
    retry: false,
    gcTime: 0,
    networkMode: "always",
    // Do not store form data in mutation variables, metadata, or cached results.
    mutationFn: async () => {
      const current = input.current;
      input.current = undefined;
      try {
        if (!current) throw submissionError();
        await submitJointAccountHolder(current.accounts, current.details);
      } catch {
        throw submissionError();
      }
    },
  });
  useEffect(
    () => () => {
      input.current = undefined;
      removeCachedMutation();
    },
    [removeCachedMutation]
  );
  const submit = async (accounts: string[], details: HolderDetails) => {
    if (inFlight.current) throw submissionError();
    inFlight.current = true;
    input.current = { accounts, details };
    try {
      await mutateAsync();
    } finally {
      input.current = undefined;
      inFlight.current = false;
      reset();
      removeCachedMutation();
    }
  };
  return { submit, isPending };
};
